import React, { useMemo, useState } from 'react';
import { Download, X, Check, FileSpreadsheet, User } from 'lucide-react';
interface ExportModalProps {
  /** All processors (deduped, sorted) available in the current pipeline. */
  processorsWithCounts: {
    name: string;
    count: number;
  }[];
  rowCount: number;
  onClose: () => void;
  onExport: (processors: string[]) => void;
}
export function ExportModal({
  processorsWithCounts,
  rowCount,
  onClose,
  onExport
}: ExportModalProps) {
  const allProcessorNames = useMemo(() => processorsWithCounts.map(p => p.name), [processorsWithCounts]);
  const [selected, setSelected] = useState<string[]>(allProcessorNames);
  const toggle = (name: string) => {
    setSelected(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };
  const totalLoans = processorsWithCounts.filter(p => selected.includes(p.name)).reduce((sum, p) => sum + p.count, 0);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-122">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden border border-slate-200 dark:border-slate-700 animate-in fade-in zoom-in-95 duration-200" data-id="element-123">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50" data-id="element-124">
          <div className="flex items-center space-x-3" data-id="element-125">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" data-id="element-126" />
            <div data-id="element-127">
              <h2 className="text-base font-semibold text-slate-900 dark:text-white" data-id="element-128">
                Export Pipeline
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400" data-id="element-129">
                {rowCount} filtered {rowCount === 1 ? 'loan' : 'loans'} · one
                sheet per processor
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" data-id="element-130">
            <X className="w-5 h-5" data-id="element-131" />
          </button>
        </div>

        <div className="p-6 space-y-4" data-id="element-132">
          <div data-id="element-133">
            <div className="flex items-center justify-between mb-2" data-id="element-134">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300" data-id="element-135">
                Processors to include
              </h3>
              <div className="flex items-center space-x-3 text-xs" data-id="element-136">
                <button onClick={() => setSelected(allProcessorNames)} className="text-cobalt-600 dark:text-cobalt-400 hover:underline" data-id="element-137">
                  Select all
                </button>
                <button onClick={() => setSelected([])} className="text-slate-500 dark:text-slate-400 hover:underline" data-id="element-138">
                  Clear
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2" data-id="element-139">
              Each selected processor becomes its own sheet in the workbook,
              containing only their loans.
            </p>
            <div className="border border-slate-200 dark:border-slate-700 rounded-md divide-y divide-slate-100 dark:divide-slate-800 max-h-72 overflow-y-auto" data-id="element-140">
              {processorsWithCounts.length === 0 && <div className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400 text-center italic" data-id="element-141">
                  No processors in the current filtered pipeline.
                </div>}
              {processorsWithCounts.map(p => {
              const checked = selected.includes(p.name);
              return <button key={p.name} type="button" onClick={() => toggle(p.name)} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60 text-left transition-colors" data-id="element-142">
                    <span className={`flex items-center justify-center w-4 h-4 rounded border flex-shrink-0 ${checked ? 'bg-cobalt-600 border-cobalt-600 text-white' : 'border-slate-300 dark:border-slate-600 text-transparent'}`} data-id="element-143">
                      <Check className="w-3 h-3" strokeWidth={3} data-id="element-144" />
                    </span>
                    <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-145" />
                    <span className="flex-1 truncate" data-id="element-146">{p.name}</span>
                    <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-slate-100 dark:bg-slate-700 text-[11px] font-semibold text-slate-600 dark:text-slate-300 flex-shrink-0" data-id="element-147">
                      {p.count}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          <div data-id="element-148">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2" data-id="element-149">
              Format
            </h3>
            <div className="flex items-center space-x-2" data-id="element-150">
              <label className="flex items-center space-x-2 px-3 py-1.5 border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 rounded-md text-sm font-medium text-emerald-700 dark:text-emerald-300 cursor-pointer" data-id="element-151">
                <input type="radio" defaultChecked name="format" className="text-emerald-600" data-id="element-152" />
                <FileSpreadsheet className="w-3.5 h-3.5" data-id="element-153" />
                <span data-id="element-154">Excel (.xlsx)</span>
              </label>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between gap-3" data-id="element-155">
          <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-156">
            {selected.length} sheet{selected.length === 1 ? '' : 's'} ·{' '}
            {totalLoans} {totalLoans === 1 ? 'loan' : 'loans'}
          </div>
          <div className="flex items-center space-x-2" data-id="element-157">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors" data-id="element-158">
              Cancel
            </button>
            <button onClick={() => onExport(selected)} disabled={selected.length === 0} className="px-4 py-2 text-sm font-medium bg-cobalt-600 hover:bg-cobalt-700 text-white rounded-md transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center" data-id="element-159">
              <Download className="w-4 h-4 mr-1.5" data-id="element-160" />
              Download Workbook
            </button>
          </div>
        </div>
      </div>
    </div>;
}